import {
  BooleanField,
  BulkUpdateButton,
  ColumnsButton,
  DataTable,
  ExportButton,
  FilterButton,
  List,
  NullableBooleanInput,
  ReferenceField,
  RichTextField,
  TextInput,
  TopToolbar,
  UpdateButton,
  useCanAccess,
} from 'react-admin';
import { CheckCircle, Replay } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { ReactElement } from 'react';
import TzDateField from '../../Components/TzDate/TzDateField';
import LargePagination from '../../Components/LargePagination';
import { TrackedErrorIssueResource } from '../../Entities/TrackedErrorIssue';
import { DEFAULT_LIST_PAGE_PAGINATION_LIMIT } from '../../utils';

const filters = [
  <NullableBooleanInput alwaysOn key="resolved" source="equals.is_resolved" label="Is Resolved" />,
  <TextInput key="category" source="contains.category" label="Category" />,
  <TextInput key="message" source="contains.last_message" label="Last Message" />,
];

function TrackedErrorIssuesListActions(): ReactElement {
  return (
    <TopToolbar>
      <ColumnsButton />
      <FilterButton />
      <ExportButton />
    </TopToolbar>
  );
}

function TrackedErrorIssuesTable({
  bulkActionButtons,
  canWriteTrackedErrors,
}: {
  bulkActionButtons: false | ReactElement;
  canWriteTrackedErrors: boolean;
}): ReactElement {
  return (
    <DataTable
      bulkActionButtons={bulkActionButtons}
      hiddenColumns={['attributes.resolved_at_utc', 'attributes.resolved_by']}
      rowClick="show"
    >
      <DataTable.Col label="Entity">
        <ReferenceField reference="merchants" source="relationships.merchant.data.id" />
      </DataTable.Col>
      <DataTable.Col source="attributes.category" label="Category" />
      <DataTable.Col source="attributes.operation" label="Operation" />
      <DataTable.Col source="attributes.is_resolved" label="Resolved">
        <BooleanField source="attributes.is_resolved" />
      </DataTable.Col>
      <DataTable.Col source="attributes.resolved_at_utc" label="Resolved At">
        <TzDateField source="attributes.resolved_at_utc" showTime serverTimezone="UTC" />
      </DataTable.Col>
      <DataTable.Col source="attributes.resolved_by" label="Resolved By" />
      <DataTable.NumberCol source="attributes.occurrence_count" label="Occurrences" />
      <DataTable.Col source="attributes.first_occurred_at_utc" label="First Occurred">
        <TzDateField source="attributes.first_occurred_at_utc" showTime serverTimezone="UTC" />
      </DataTable.Col>
      <DataTable.Col source="attributes.last_occurred_at_utc" label="Last Occurred">
        <TzDateField source="attributes.last_occurred_at_utc" showTime serverTimezone="UTC" />
      </DataTable.Col>
      <DataTable.Col source="attributes.last_message" label="Last Message">
        <RichTextField source="attributes.last_message" />
      </DataTable.Col>
      {canWriteTrackedErrors && (
        <DataTable.Col
          label="Actions"
          disableSort
          render={(record: TrackedErrorIssueResource) => (
            <Stack direction="row" spacing={1}>
              <UpdateButton
                label="Mark resolved"
                color="success"
                sx={{ color: 'text.primary' }}
                icon={<CheckCircle />}
                variant="contained"
                disabled={record.attributes.is_resolved}
                data={{ attributes: { is_resolved: true } }}
              />
              <UpdateButton
                label="Mark unresolved"
                color="warning"
                sx={{ color: 'text.primary' }}
                icon={<Replay />}
                variant="contained"
                disabled={!record.attributes.is_resolved}
                data={{ attributes: { is_resolved: false } }}
              />
            </Stack>
          )}
        />
      )}
    </DataTable>
  );
}

export default function TrackedErrorIssuesList(): ReactElement {
  const trackedErrorWriteAccess = useCanAccess({
    action: 'write',
    resource: 'tracked_error_issues',
  });

  const bulkActionButtons = trackedErrorWriteAccess.canAccess ? (
    <Stack direction="row" spacing={1}>
      <BulkUpdateButton
        label="Mark resolved"
        color="success"
        sx={{ color: 'text.primary' }}
        icon={<CheckCircle />}
        variant="contained"
        data={{ attributes: { is_resolved: true } }}
      />
      <BulkUpdateButton
        label="Mark unresolved"
        color="warning"
        icon={<Replay />}
        variant="contained"
        sx={{ color: 'text.primary' }}
        data={{ attributes: { is_resolved: false } }}
      />
    </Stack>
  ) : (
    false
  );

  return (
    <List<TrackedErrorIssueResource>
      actions={<TrackedErrorIssuesListActions />}
      filterDefaultValues={{ equals: { is_resolved: false } }}
      filters={filters}
      pagination={<LargePagination />}
      perPage={DEFAULT_LIST_PAGE_PAGINATION_LIMIT}
      queryOptions={{ meta: { prefetch: ['merchant'] } }}
      resource="tracked_error_issues"
      sort={{ field: 'attributes.last_occurred_at_utc', order: 'DESC' }}
    >
      <TrackedErrorIssuesTable
        bulkActionButtons={bulkActionButtons}
        canWriteTrackedErrors={trackedErrorWriteAccess.canAccess ?? false}
      />
    </List>
  );
}
